/** Mis Documentos: Préstamos Activos + Otros Documentos (idéntico al módulo actual). */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { misDocumentos, misOtrosDocumentos } from '../servicios/consultas';
import { api } from '../servicios/api';
import { Badge, Encabezado, alias } from '../componentes/Comunes';
import { fmtFecha, mensajeError } from '../util';
import type { Prestamo, OtroDocumento } from '@cji/shared';

export function MisDocumentos() {
  const { usuario } = useAuth();
  const [items, setItems] = useState<Prestamo[]>([]);
  const [otros, setOtros] = useState<OtroDocumento[]>([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState<{ tipo: string; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!usuario?.email) return;
    setCargando(true);
    try {
      const [ps, os] = await Promise.all([misDocumentos(usuario.email), misOtrosDocumentos(usuario.email)]);
      setItems(ps);
      setOtros(os);
    } catch (e) {
      setAviso({ tipo: 'error', texto: mensajeError(e) });
    } finally {
      setCargando(false);
    }
  }, [usuario]);

  useEffect(() => { void cargar(); }, [cargar]);

  async function accion(fn: () => Promise<unknown>, id: string, ok: string) {
    setOcupado(id);
    setAviso(null);
    try {
      await fn();
      setAviso({ tipo: 'ok', texto: ok });
      await cargar();
    } catch (e) {
      setAviso({ tipo: 'error', texto: mensajeError(e) });
    } finally {
      setOcupado(null);
    }
  }

  return (
    <main className="pagina">
      <Encabezado icono="🗂️" titulo="Mis Documentos" />

      <div className="tarjeta">
        <div className="tarjeta-titulo">🗂 Mis Documentos Pendientes</div>
        <p className="subtitulo" style={{ margin: 0 }}>
          Esta página muestra todos los documentos, préstamos y ubicaciones asignados a usted que requieren atención.
          <br />Usuario actual: <strong style={{ color: 'var(--verde-700)' }}>{alias(usuario?.email)}</strong>
        </p>
      </div>

      {aviso && <div className={`aviso ${aviso.tipo}`}>{aviso.texto}</div>}

      {/* --- Préstamos Activos --- */}
      <div className="tarjeta">
        <div className="tarjeta-titulo">⇄ Préstamos Activos <span className="contador">{items.length}</span></div>
        {cargando ? (
          <div className="cargando">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="vacio">No tiene préstamos activos.</div>
        ) : (
          <table className="tabla">
            <thead><tr><th>Fecha</th><th>Radicado</th><th>Asignado</th><th>Estado</th><th style={{ textAlign: 'right' }}>Acciones</th></tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td>{fmtFecha(p.fechas?.creacion)}</td>
                  <td><Link to={`/expediente/${p.expedienteId}`}><strong>{p.radicadoHijo}</strong></Link></td>
                  <td>{alias(usuario?.email)}</td>
                  <td><Badge estado={p.estado} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="fila" style={{ justifyContent: 'flex-end' }}>
                      {p.estado === 'Pendiente' && (
                        <>
                          <button className="btn btn-verde btn-sm" disabled={ocupado === p.id}
                            onClick={() => void accion(() => api.aceptarPrestamo({ expedienteId: p.expedienteId, prestamoId: p.id }), p.id, 'Préstamo aceptado.')}>Aceptar</button>
                          <button className="btn btn-secundario btn-sm" disabled={ocupado === p.id}
                            onClick={() => void accion(() => api.cancelarPrestamo({ expedienteId: p.expedienteId, prestamoId: p.id }), p.id, 'Solicitud rechazada.')}>Rechazar</button>
                        </>
                      )}
                      {(p.estado === 'Aceptado' || p.estado === 'Entregado') && (
                        <>
                          <Link className="btn btn-secundario btn-sm" to={`/expediente/${p.expedienteId}`}>Endosar</Link>
                          <button className="btn btn-rojo btn-sm" disabled={ocupado === p.id}
                            onClick={() => void accion(() => api.devolverPrestamo({ expedienteId: p.expedienteId, prestamoId: p.id }), p.id, 'Préstamo devuelto.')}>Devolver</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* --- Otros Documentos --- */}
      <div className="tarjeta">
        <div className="tarjeta-titulo">📁 Otros Documentos <span className="contador">{otros.length}</span></div>
        {otros.length === 0 ? (
          <div className="vacio" style={{ padding: 24 }}>Sin otros documentos.</div>
        ) : (
          <table className="tabla">
            <thead><tr><th>Fecha</th><th>Radicado</th><th>Asignado</th><th>Folios</th><th>Estado</th><th>Descripción</th></tr></thead>
            <tbody>
              {otros.map((o) => (
                <tr key={o.id}>
                  <td>{fmtFecha(o.creadoEn)}</td>
                  <td>{o.radicado || '—'}</td>
                  <td>{alias(usuario?.email)}</td>
                  <td>{o.folios}</td>
                  <td><Badge estado={o.estado} /></td>
                  <td>{o.descripcion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
